// InfoTip — small "i" icon that reveals a hover tooltip.
//
// Used to attach a "what is this field / how do I fill it" hint next to
// every editor label without cluttering the UI. Editor-only — read view
// keeps clean labels.
//
// Hover or focus opens the tooltip; blur/mouseleave closes it. Positions
// to the right of the icon by default; when there's no room on the right
// (near the viewport edge), it flips to the left.
import { useLayoutEffect, useRef, useState } from "react";

export function InfoTip({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const bubbleRef = useRef<HTMLSpanElement>(null);

  // If the tooltip would overflow past the right edge, flip it to the
  // left of the icon. Runs whenever we open so page resize between opens
  // still gets a chance to re-measure.
  useLayoutEffect(() => {
    if (!open || !bubbleRef.current) return;
    const rect = bubbleRef.current.getBoundingClientRect();
    setFlip(rect.right > window.innerWidth - 8);
  }, [open]);

  return (
    <span
      className="relative inline-flex items-center align-middle"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        tabIndex={0}
        aria-label="More info"
        className="ml-1 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-border-hover bg-card text-[9px] font-semibold normal-case tracking-normal text-text-tertiary hover:border-accent hover:text-accent focus:outline-none"
      >
        i
      </button>
      {open && (
        <span
          ref={bubbleRef}
          role="tooltip"
          className={`absolute top-1/2 z-30 w-64 -translate-y-1/2 rounded-md border border-border bg-card p-2.5 text-[11px] font-normal normal-case leading-snug tracking-normal text-text shadow-lg ${
            flip ? "right-full mr-2" : "left-full ml-2"
          }`}
        >
          {children}
        </span>
      )}
    </span>
  );
}
